import { Capacitor } from '@capacitor/core';

const CONSOLE_LEVELS = ['log', 'info', 'warn', 'error', 'debug'] as const;

type ConsoleLevel = (typeof CONSOLE_LEVELS)[number];

let originals: Partial<Record<ConsoleLevel, (...args: unknown[]) => void>> | null = null;

/**
 * Android/iOS WebViews forward console output as a single string via
 * `WebChromeClient.onConsoleMessage`. Objects become `[object Object]`.
 * Serialize args to JSON so Logcat stays readable.
 */
export function serializeConsoleArg(value: unknown): string {
	if (typeof value === 'string') {
		return value;
	}

	if (
		typeof value === 'number'
		|| typeof value === 'boolean'
		|| value === null
		|| value === undefined
		|| typeof value === 'bigint'
		|| typeof value === 'symbol'
	) {
		return String(value);
	}

	if (value instanceof Error) {
		return value.stack ?? `${value.name}: ${value.message}`;
	}

	try {
		const seen = new WeakSet<object>();

		return JSON.stringify(value, (_key, nested: unknown) => {
			if (typeof nested === 'object' && nested !== null) {
				if (seen.has(nested)) {
					return '[Circular]';
				}

				seen.add(nested);
			}

			return nested;
		});
	} catch {
		return Object.prototype.toString.call(value);
	}
}

/** Patches `console.*` on native so Capacitor Logcat shows JSON instead of `[object Object]`. */
export function installNativeConsoleSerialization(): void {
	if (originals !== null || !Capacitor.isNativePlatform()) {
		return;
	}

	originals = {};

	for (const level of CONSOLE_LEVELS) {
		const original = console[level].bind(console) as (...args: unknown[]) => void;
		originals[level] = original;
		console[level] = (...args: unknown[]) => {
			original(args.map(serializeConsoleArg).join(' '));
		};
	}
}

/** @internal Restores console methods between unit tests. */
export function resetNativeConsoleSerializationForTests(): void {
	if (!originals) {
		return;
	}

	for (const level of CONSOLE_LEVELS) {
		const original = originals[level];
		if (original) {
			console[level] = original;
		}
	}

	originals = null;
}
